import { config } from "config";

export interface IAffiliateReferral {
  campaignKey?: string;
  slug?: string;
  affiliateId: string;
  createdAt: string;
  expiresAt: string;
}

interface IAffiliateReferralStorage {
  platformReferral?: IAffiliateReferral;
  campaignReferrals: Record<string, IAffiliateReferral>;
}

const storageKey = `@${config.appAgent}:affiliate-referrals`;

function getEmptyStorage(): IAffiliateReferralStorage {
  return {
    platformReferral: undefined,
    campaignReferrals: {},
  };
}

function canUseLocalStorage() {
  return typeof window !== "undefined" && Boolean(window.localStorage);
}

function readReferralStorage(): IAffiliateReferralStorage {
  if (!canUseLocalStorage()) return getEmptyStorage();

  try {
    const rawStorage = localStorage.getItem(storageKey);
    if (!rawStorage) return getEmptyStorage();

    const parsedStorage = JSON.parse(rawStorage);
    return {
      platformReferral: parsedStorage?.platformReferral,
      campaignReferrals: parsedStorage?.campaignReferrals || {},
    };
  } catch {
    return getEmptyStorage();
  }
}

function writeReferralStorage(storage: IAffiliateReferralStorage) {
  if (!canUseLocalStorage()) return;
  localStorage.setItem(storageKey, JSON.stringify(storage));
}

function isExpired(referral?: IAffiliateReferral) {
  if (!referral?.expiresAt) return false;
  const expiresAt = new Date(referral.expiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt < Date.now();
}

export function cleanExpiredAffiliateReferrals() {
  const storage = readReferralStorage();
  const campaignReferrals = Object.entries(storage.campaignReferrals).reduce<
    Record<string, IAffiliateReferral>
  >((acc, [key, referral]) => {
    if (!isExpired(referral)) acc[key] = referral;
    return acc;
  }, {});

  writeReferralStorage({
    platformReferral: isExpired(storage.platformReferral)
      ? undefined
      : storage.platformReferral,
    campaignReferrals,
  });
}

export function savePlatformAffiliateReferral(referral: IAffiliateReferral) {
  cleanExpiredAffiliateReferrals();
  const storage = readReferralStorage();

  writeReferralStorage({
    platformReferral: storage.platformReferral || referral,
    campaignReferrals: storage.campaignReferrals,
  });
}

export function saveCampaignAffiliateReferral(referral: IAffiliateReferral) {
  if (!referral?.campaignKey) return;

  cleanExpiredAffiliateReferrals();
  const storage = readReferralStorage();

  writeReferralStorage({
    platformReferral: storage.platformReferral,
    campaignReferrals: {
      ...storage.campaignReferrals,
      [referral.campaignKey]: referral,
    },
  });
}

export function getCampaignAffiliateReferral(key?: string) {
  if (!key) return undefined;
  const storage = readReferralStorage();
  return storage.campaignReferrals[key];
}

export function getPlatformAffiliateReferral() {
  const storage = readReferralStorage();
  return storage.platformReferral;
}

export function clearPlatformAffiliateReferral() {
  const storage = readReferralStorage();
  writeReferralStorage({
    ...storage,
    platformReferral: undefined,
  });
}
